import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useOffline } from '@/context/OfflineContext';
import api, { apiHelpers } from '@/lib/api';
import { queueOfflineAction, OFFLINE_ACTIONS } from '@/lib/offlineQueue';
import { saveFieldNotesDraft, getFieldNotesDraft, clearFieldNotesDraft } from '@/lib/offlineSync';
import { useFieldNotesSave } from '@/hooks/useFieldNotesSave';
import { useDeveloperMode } from '@/hooks/useDeveloperMode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { PermissionGate, usePermission } from '@/components/PermissionGate';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  Edit,
  Trash2,
  FileText,
  CheckCircle,
  Camera,
  Check,
  Save,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import PhotoLightbox from '@/components/PhotoLightbox';

const SiteVisitDetailPage = () => {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const { organization, user } = useAuth();
  const { t } = useLanguage();
  const { isOnline } = useOffline();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  
  const [visit, setVisit] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [hasPendingOfflineNotes, setHasPendingOfflineNotes] = useState(false);
  const [estimateMissing, setEstimateMissing] = useState(false);
  const [isResettingConversion, setIsResettingConversion] = useState(false);
  // Unified save state machine — single source of truth for save/sync.
  const saveHook = useFieldNotesSave({ organization, user, visitId });
  const developerMode = useDeveloperMode();
  const [fieldNotesData, setFieldNotesData] = useState({
    internalNotes: '',
    clientNotes: '',
    internalVoiceRecordings: [],
    clientVoiceRecordings: [],
    clientExpectations: {
      wantsDone: '',
      timeline: '',
      budgetRange: '',
      specialRequests: ''
    },
    measurements: [],
    photos: [],
    sketches: [],
    siteConditions: [],
    customConditions: [],
    checklist: [],
    isChecklistComplete: false
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const loadVisit = useCallback(async () => {
    if (!organization?.id || !visitId) return;
    
    try {
      const [visitRes, employeesRes] = await Promise.all([
        apiHelpers.getSiteVisit(organization.id, visitId),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees`).then(r => r.json())
      ]);
      
      setVisit(visitRes.data);
      setEmployees(employeesRes || []);
      setEditData(visitRes.data);
      
      // If visit is converted, check if the linked estimate actually exists
      if (visitRes.data.status === 'converted' && visitRes.data.converted_estimate_id) {
        try {
          await apiHelpers.getEstimate(organization.id, visitRes.data.converted_estimate_id);
          setEstimateMissing(false);
        } catch (estimateError) {
          console.warn('Linked estimate not found:', visitRes.data.converted_estimate_id);
          setEstimateMissing(true);
        }
      } else {
        setEstimateMissing(false);
      }
      
      // Check for offline draft first
      let offlineDraft = null;
      try {
        offlineDraft = await getFieldNotesDraft(visitId);
      } catch (e) {
        // IndexedDB may not be available
      }
      
      // Cleanup: stale draft with notes=null (legacy bug from earlier sessions)
      if (offlineDraft && offlineDraft.notes === null) {
        try { await clearFieldNotesDraft(visitId); } catch { /* ignore */ }
      }
      
      // Pick the source of truth between local draft and server.
      // CRITICAL: compare timestamps. Server-newer wins (cross-device case).
      // If draft is newer, just use it and let the page's own autosave timer
      // sync it to the server in the background — do NOT auto-POST here.
      // That auto-POST was firing a misleading "Offline notes synced
      // successfully" toast every time the page loaded, regardless of whether
      // the user was ever offline.
      const visitData = visitRes.data;
      const serverTs = visitData.last_edited_at || visitData.updated_at || null;
      const draftTs = offlineDraft?.updated_at || null;

      let useDraft = false;
      if (offlineDraft && offlineDraft.notes && !offlineDraft.synced) {
        // SolidHome safety rule: if the server doc is EFFECTIVELY EMPTY but
        // the local draft has real content, ALWAYS prefer the draft —
        // regardless of timestamps. Recovers an iPad's unsynced session
        // even when an unrelated server-side update bumped updated_at.
        const serverEffectivelyEmpty = !visitData.internal_notes && !visitData.project_notes
          && (!visitData.structured_measurements || visitData.structured_measurements.length === 0)
          && (!visitData.photos || visitData.photos.length === 0);
        const dn = offlineDraft.notes;
        const draftHasContent = (
          (dn.internalNotes && dn.internalNotes.trim()) ||
          (dn.clientNotes && dn.clientNotes.trim()) ||
          (Array.isArray(dn.measurements) && dn.measurements.length > 0) ||
          (Array.isArray(dn.photos) && dn.photos.length > 0)
        );

        if (serverEffectivelyEmpty && draftHasContent) {
          useDraft = true;
          console.warn('[SiteVisitDetail] Server empty but draft has content — recovering from local draft', { draftTs });
          toast.success('Restored unsaved work from this device', {
            description: 'Tap Save to push it to the cloud.',
            duration: 8000,
          });
        } else if (!serverTs || !draftTs) {
          // One side has no timestamp — fall back to draft (safer)
          useDraft = true;
        } else {
          try {
            useDraft = new Date(draftTs).getTime() >= new Date(serverTs).getTime();
          } catch {
            useDraft = true;
          }
        }
      }

      if (useDraft) {
        console.log('[SiteVisitDetail] Using local draft (newer than server or no server ts)');
        setFieldNotesData(offlineDraft.notes);
        setHasPendingOfflineNotes(true);
        // Do NOT auto-POST here. The page's autosave timer + explicit Save
        // both handle server sync with proper, honest toasts.
      } else {
        // Use server data. CRITICAL: do NOT auto-clear the local draft when
        // server appears newer. The server's updated_at can advance for
        // unrelated reasons (status flip, completion snapshot, autosave on
        // a sibling tab) while the iPad still holds the user's only copy
        // of the notes. Wiping the draft on every load was the SolidHome
        // silent-data-loss bug (Feb 2026). Drafts only get cleared after
        // an explicit successful manual save (see useFieldNotesSave.save).
        if (offlineDraft) {
          console.log('[SiteVisitDetail] Server data is newer than local draft — using server, draft kept as backup');
        }
        setFieldNotesData({
          internalNotes: visitData.internal_notes || '',
          clientNotes: visitData.project_notes || '',
          internalVoiceRecordings: visitData.internal_voice_recordings || [],
          clientVoiceRecordings: visitData.client_voice_recordings || [],
          clientExpectations: visitData.client_expectations || {
            wantsDone: '',
            timeline: '',
            budgetRange: '',
            specialRequests: ''
          },
          measurements: visitData.structured_measurements || [],
          photos: visitData.photos || [],
          sketches: visitData.sketches || [],
          siteConditions: visitData.site_conditions || [],
          customConditions: visitData.custom_conditions || [],
          checklist: visitData.checklist || [],
          isChecklistComplete: visitData.is_checklist_complete || false
        });
        setHasPendingOfflineNotes(false);
      }
    } catch (error) {
      console.error('Failed to load site visit:', error);
      
      // If offline, try to load from offline draft
      if (!isOnline) {
        try {
          const offlineDraft = await getFieldNotesDraft(visitId);
          if (offlineDraft) {
            setFieldNotesData(offlineDraft.notes);
            setHasPendingOfflineNotes(true);
            toast.info('Loaded from offline draft');
            setIsLoading(false);
            return;
          }
        } catch (e) {
          // IndexedDB unavailable
        }
      }
      
      toast.error('Failed to load site visit');
      navigate('/site-visits');
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, visitId, navigate, isOnline, user?.id, user?.name]);

  useEffect(() => {
    loadVisit();
  }, [loadVisit]);

  const handleSave = async () => {
    try {
      await apiHelpers.updateSiteVisit(
        organization.id,
        visitId,
        editData,
        user.id,
        user.name
      );
      
      toast.success('Site visit updated');
      setIsEditing(false);
      loadVisit();
    } catch (error) {
      console.error('Failed to update site visit:', error);
      toast.error('Failed to update site visit');
    }
  };

  const handleDelete = async () => {
    try {
      await apiHelpers.deleteSiteVisit(organization.id, visitId, user.id);
      toast.success('Site visit deleted');
      navigate('/site-visits');
    } catch (error) {
      console.error('Failed to delete site visit:', error);
      toast.error('Failed to delete site visit');
    }
  };

  const handleMarkCompleted = async (opts = {}) => {
    // Check if checklist is complete
    if (fieldNotesData.checklist.length > 0 && !fieldNotesData.isChecklistComplete && !opts.force) {
      const incompleteItems = fieldNotesData.checklist.filter(item => item.text && !item.checked);
      if (incompleteItems.length > 0) {
        toast.error('Please complete all checklist items before marking as complete', {
          description: `${incompleteItems.length} item(s) remaining — opening field notes…`
        });
        navigate(`/site-visits/${visitId}/field-notes`);
        return;
      }
    }

    try {
      // Calls the snapshot-aware /complete endpoint. This creates an immutable
      // Completion Snapshot (v1, v2, …) AND flips status to "completed" in one
      // atomic operation on the server.
      const res = await apiHelpers.completeSiteVisit(
        organization.id,
        visitId,
        user.id,
        user.name,
        !!opts.force,
      );

      const version = res?.data?.version;
      toast.success(
        version && version > 1
          ? `Site visit re-snapshotted (v${version})`
          : 'Site visit marked complete — snapshot saved',
        {
          description: 'PDFs and job sheets will always use this snapshot.',
        }
      );
      loadVisit();
    } catch (error) {
      console.error('Failed to complete site visit:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark site visit complete');
    }
  };

  const handleConvertToEstimate = async () => {
    try {
      const response = await apiHelpers.convertSiteVisitToEstimate(
        organization.id,
        visitId,
        user.id,
        user.name
      );
      
      const data = response.data;
      
      if (data.needs_review) {
        toast.success('Estimate created and sent for manager review');
      } else {
        toast.success('Estimate created successfully');
      }
      
      setShowConvertDialog(false);
      
      // Navigate to the new estimate with a brief delay to ensure DB consistency
      setTimeout(() => {
        navigate(`/estimates/${data.estimate_id}`);
      }, 300);
    } catch (error) {
      console.error('Failed to convert to estimate:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert to estimate');
    }
  };

  // Handle resetting a failed conversion (orphan recovery)
  // Since UI only shows reset button when estimateMissing=true, we know the estimate can't be loaded
  // So we should use force=true by default to handle corrupted estimates
  const handleResetConversion = async (force = true) => {
    setIsResettingConversion(true);
    try {
      await apiHelpers.resetSiteVisitConversion(
        organization.id,
        visitId,
        user.id,
        user.name,
        force
      );
      toast.success('Conversion reset - you can now try converting again');
      setEstimateMissing(false);
      loadVisit();
    } catch (error) {
      console.error('Failed to reset conversion:', error);
      const errorDetail = error.response?.data?.detail || '';
      
      // If the estimate is actually valid (rare case), show appropriate message
      if (errorDetail.includes('valid')) {
        toast.error('The linked estimate exists and is valid. Try refreshing the page.');
      } else if (errorDetail.includes('corrupted') && !force) {
        // Shouldn't happen since we default to force=true, but handle it
        const confirmForce = await showConfirm({
          title: 'Reset Corrupted Estimate?',
          description: 'The linked estimate exists but is corrupted (the client may have been deleted). Do you want to delete the corrupted estimate and reset this site visit?',
          variant: 'warning',
          confirmText: 'Reset',
          cancelText: 'Cancel'
        });
        if (confirmForce) {
          await handleResetConversion(true);
          return;
        }
      } else {
        toast.error(errorDetail || 'Failed to reset conversion');
      }
    } finally {
      setIsResettingConversion(false);
    }
  };

  // Helper to format field notes for API
  const formatFieldNotesForAPI = (notes) => ({
    project_notes: notes.clientNotes,
    internal_notes: notes.internalNotes,
    internal_voice_recordings: notes.internalVoiceRecordings?.map(r => ({
      id: r.id,
      transcript: r.transcript,
      timestamp: r.timestamp
    })) || [],
    client_voice_recordings: notes.clientVoiceRecordings?.map(r => ({
      id: r.id,
      transcript: r.transcript,
      timestamp: r.timestamp
    })) || [],
    client_expectations: notes.clientExpectations,
    structured_measurements: notes.measurements,
    photos: notes.photos,
    sketches: notes.sketches || [],
    site_conditions: notes.siteConditions,
    custom_conditions: notes.customConditions,
    checklist: notes.checklist,
    is_checklist_complete: notes.isChecklistComplete
  });

  // Strip base64 from photo payloads before sending to server. Photos with only
  // dataUrls (still uploading in background via PhotoUploadManager) are NOT
  // sent to the server at all — sending them as `local_only:true` placeholders
  // pollutes the visit's photos array with URL-less entries that render as
  // broken images. The PhotoUploadManager will sync them later and the next
  // save will pick them up with real URLs.
  const stripPhotoBlobs = (notes) => {
    if (!notes.photos || notes.photos.length === 0) return notes;
    const slim = notes.photos
      .filter(p => p.url || p.server_id) // only send photos that actually have a server URL
      .map(p => ({
        id: p.id,
        url: p.url || p.dataUrl,
        server_id: p.server_id,
        name: p.name,
        tag: p.tag,
        timestamp: p.timestamp,
      }));
    return { ...notes, photos: slim };
  };

  // Eagerly process any pending photo uploads BEFORE saving text.
  // This makes a manual "Save" feel synchronous: photos that uploaded in time
  // get URLs in the saved payload; photos still in flight remain local_only
  // and the next save (or background tick) will reconcile them.
  const flushPhotoUploads = async () => {
    try {
      const { processPhotoUploads } = await import('@/lib/photoUploadManager');
      const result = await processPhotoUploads();
      return result.uploaded; // [{photoId, serverUrl, serverId, siteVisitId}]
    } catch {
      return [];
    }
  };

  const handleSaveFieldNotes = async (notes) => {
    // Mirror UI state immediately (so the editor reflects what the user
    // sees), then delegate the actual save to the unified state machine.
    setFieldNotesData(notes);
    // Flush in-flight photo uploads so URLs land in this save. Best-effort.
    try {
      const uploaded = await flushPhotoUploads();
      if (uploaded.length > 0) {
        notes = {
          ...notes,
          photos: (notes.photos || []).map(p => {
            const match = uploaded.find(u => u.siteVisitId === visitId && (p.local_only || !p.url));
            return match ? { ...p, url: match.serverUrl, server_id: match.serverId, local_only: false } : p;
          }),
        };
        setFieldNotesData(notes);
      }
    } catch { /* ignore — photos sync independently */ }

    const result = await saveHook.save(notes, { mode: 'manual' });
    if (result?.ok) {
      setHasPendingOfflineNotes(false);
      loadVisit();
    } else {
      // Queue holds the change; surface that to the legacy "pending" flag
      // so the OfflineBanner and other places update.
      setHasPendingOfflineNotes(true);
    }
  };

  // Background autosave — delegates to the same state machine. Silent on
  // success/failure (no toasts) per the hook's mode='auto' contract.
  const handleAutoSaveFieldNotes = useCallback(async (notes) => {
    if (!organization?.id || !visitId) return;
    const result = await saveHook.autoSave(notes);
    if (!result?.ok) setHasPendingOfflineNotes(true);
  }, [organization?.id, visitId, saveHook]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-700 text-base px-3 py-1">Scheduled</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700 text-base px-3 py-1">Completed</Badge>;
      case 'converted':
        return <Badge className="bg-purple-100 text-purple-700 text-base px-3 py-1">Converted to Estimate</Badge>;
      case 'pending_review':
        return <Badge className="bg-amber-100 text-amber-700 text-base px-3 py-1">Pending Review</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const primaryColor = organization?.brand_primary_color || '#1e3a8a';
  const accentColor = organization?.brand_accent_color || '#fbbf24';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Site visit not found</p>
        <Button onClick={() => navigate('/site-visits')} className="mt-4">
          Back to Site Visits
        </Button>
      </div>
    );
  }

  const allPhotos = [...(visit.photos || [])];
  const isStaff = user?.role === 'staff';
  // Field notes checklist must be explicitly completed before conversion (no status-based bypass)
  const fieldNotesCompleted = visit.is_checklist_complete === true;
  // Visit must be marked completed before conversion is allowed (defeats purpose of Mark Completed otherwise)
  const visitCompleted = visit.status === 'completed';
  // Allow conversion only if visit is completed AND checklist is complete,
  // OR if already converted but estimate is missing (recovery)
  const canConvert = (visitCompleted && fieldNotesCompleted) || (visit.status === 'converted' && estimateMissing);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="site-visit-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/site-visits')}
            className="rounded-xl"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-foreground truncate">
                {visit.client_name}
              </h1>
              {getStatusBadge(visit.status)}
            </div>
            {visit.service_type && (
              <p className="text-muted-foreground mt-1">{visit.service_type}</p>
            )}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {visit.status !== 'converted' && (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/site-visits/${visitId}/field-notes`)}
                className={`rounded-xl ${hasPendingOfflineNotes ? 'border-amber-400 bg-amber-50' : ''}`}
                data-testid="field-notes-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Field Notes
                {hasPendingOfflineNotes && (
                  <Badge className="ml-2 bg-amber-500 text-white text-xs px-1.5">
                    Pending Sync
                  </Badge>
                )}
              </Button>
              {visit.last_edited_at && (
                <p className="text-[11px] text-muted-foreground mt-1.5 ml-1" data-testid="field-notes-audit">
                  Last edited{visit.last_edited_by_user_name ? ` by ${visit.last_edited_by_user_name}` : ''} ·{' '}
                  {new Date(visit.last_edited_at).toLocaleString(undefined, {
                    month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })}
                </p>
              )}
              
              {visit.status === 'scheduled' && (
                <Button
                  variant="outline"
                  onClick={handleMarkCompleted}
                  className="rounded-xl text-green-600 border-green-200 hover:bg-green-50"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Completed
                </Button>
              )}
              
              {canConvert && (
                <PermissionGate permission="create_draft_estimates" fallback={
                  !fieldNotesCompleted ? null : (
                    <div className="text-xs text-muted-foreground italic px-2 py-1">
                      Only managers/admins can convert to estimate
                    </div>
                  )
                }>
                  <Button
                    onClick={() => setShowConvertDialog(true)}
                    className="rounded-xl"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="convert-to-estimate-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {isStaff ? 'Create Draft Estimate' : 'Convert to Estimate'}
                  </Button>
                </PermissionGate>
              )}
              
              {/* Show hint when prerequisites for conversion not met */}
              {!canConvert && visit.status !== 'converted' && (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-xl border border-amber-200" data-testid="field-notes-required-hint">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>
                    {!visitCompleted
                      ? 'Mark this site visit as Completed before creating an estimate'
                      : t('complete_checklist_to_convert')}
                  </span>
                </div>
              )}
            </>
          )}
          
          {visit.converted_estimate_id && !estimateMissing && (
            <Link to={`/estimates/${visit.converted_estimate_id}`}>
              <Button variant="outline" className="rounded-xl text-purple-600 border-purple-200 hover:bg-purple-50">
                <ExternalLink className="w-4 h-4 mr-2" />
                View Estimate
              </Button>
            </Link>
          )}
          
          {/* Show recovery option if estimate is missing */}
          {estimateMissing && (
            <Button
              variant="outline"
              onClick={handleResetConversion}
              disabled={isResettingConversion}
              className="rounded-xl text-amber-600 border-amber-200 hover:bg-amber-50"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {isResettingConversion ? 'Resetting...' : 'Reset & Retry Conversion'}
            </Button>
          )}
        </div>
      </div>

      {/* Warning banner if estimate is missing */}
      {estimateMissing && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">Estimate Not Found</p>
            <p className="text-sm text-amber-700">
              This site visit shows as converted, but the linked estimate doesn't exist. 
              This may have happened due to a connection issue during conversion. 
              Click "Reset & Retry Conversion" above to try again.
            </p>
          </div>
        </div>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visit Details Card */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold">Visit Details</h2>
              {!isEditing && visit.status !== 'converted' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="text-muted-foreground"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            {isEditing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('job_address_label')}</Label>
                  <Input
                    value={editData.job_address || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, job_address: e.target.value }))}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t('date_label')}</Label>
                    <Input
                      type="date"
                      value={editData.scheduled_date || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('time_label')}</Label>
                    <Input
                      type="time"
                      value={editData.scheduled_time || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('service_type_label')}</Label>
                  <Select
                    value={editData.service_type || ''}
                    onValueChange={(value) => setEditData(prev => ({ ...prev, service_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('select_service_type')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(organization?.service_types_enabled || ['Concrete', 'Landscaping', 'Roofing', 'General']).map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('assigned_to')}</Label>
                  <Select
                    value={editData.assigned_estimator_id || "unassigned"}
                    onValueChange={(value) => setEditData(prev => ({ ...prev, assigned_estimator_id: value === "unassigned" ? "" : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select crew member" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {employees.filter(e => e.is_active).map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSave} className="rounded-xl" style={{ backgroundColor: primaryColor }}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => { setIsEditing(false); setEditData(visit); }} className="rounded-xl">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">{t('job_address_label')}</p>
                    <p className="text-muted-foreground">{visit.job_address || 'Not specified'}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">Scheduled Date</p>
                    <p className="text-muted-foreground">
                      {visit.scheduled_date ? format(parseISO(visit.scheduled_date), 'EEEE, MMMM d, yyyy') : 'Not scheduled'}
                    </p>
                  </div>
                </div>
                
                {visit.scheduled_time && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">{t('time_label')}</p>
                      <p className="text-muted-foreground">{visit.scheduled_time}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">{t('assigned_to')}</p>
                    <p className="text-muted-foreground">{visit.assigned_estimator_name || 'Unassigned'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h2 className="font-heading text-lg font-bold mb-4">Project Notes</h2>
            {(visit.project_notes || visit.internal_notes || fieldNotesData.internalNotes || fieldNotesData.clientNotes) ? (
              <div className="space-y-4">
                {/* Show internal notes */}
                {(visit.internal_notes || fieldNotesData.internalNotes) && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Internal Notes</p>
                    <p className="text-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                      {visit.internal_notes || fieldNotesData.internalNotes}
                    </p>
                  </div>
                )}
                {/* Show project/client notes */}
                {(visit.project_notes || fieldNotesData.clientNotes) && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Client Notes</p>
                    <p className="text-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                      {visit.project_notes || fieldNotesData.clientNotes}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground italic">No notes yet. Use Field Notes to add observations.</p>
            )}
          </div>

          {/* Measurements Section */}
          {((visit.structured_measurements && visit.structured_measurements.length > 0) || 
            (visit.structuredMeasurements && visit.structuredMeasurements.length > 0) ||
            (fieldNotesData.measurements && fieldNotesData.measurements.length > 0)) && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="measurements-section">
              <h2 className="font-heading text-lg font-bold mb-4">{t('measurements')}</h2>
              <div className="space-y-2">
                {(visit.structured_measurements || visit.structuredMeasurements || fieldNotesData.measurements || []).map((m, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-sm">{m.label || m.name || `Measurement ${idx + 1}`}</span>
                    <span className="text-sm font-semibold">{m.value} {m.unit || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Client Expectations & Special Requests */}
          {(() => {
            const exp = visit.client_expectations || fieldNotesData.clientExpectations || {};
            // Handle both legacy string format and new structured object format
            const isString = typeof exp === 'string';
            const wantsDone = isString ? exp : (exp.wantsDone || '');
            const timeline = isString ? '' : (exp.timeline || '');
            const budgetRange = isString ? '' : (exp.budgetRange || '');
            const specialRequests = isString
              ? (visit.special_requests || '')
              : (exp.specialRequests || visit.special_requests || '');
            const hasAny = wantsDone || timeline || budgetRange || specialRequests;
            if (!hasAny) return null;
            return (
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="font-heading text-lg font-bold mb-4">Client Requirements</h2>
                <div className="space-y-3">
                  {wantsDone && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">What Client Wants Done</p>
                      <p className="text-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg text-sm">
                        {wantsDone}
                      </p>
                    </div>
                  )}
                  {timeline && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Timeline</p>
                      <p className="text-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg text-sm">
                        {timeline}
                      </p>
                    </div>
                  )}
                  {budgetRange && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Budget Range</p>
                      <p className="text-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg text-sm">
                        {budgetRange}
                      </p>
                    </div>
                  )}
                  {specialRequests && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Special Requests</p>
                      <p className="text-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg text-sm">
                        {specialRequests}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Checklist Section — shows the field-notes checklist on the
              site visit summary so contractors don't have to open the editor
              to see what's done. Was missing from the read-only summary view. */}
          {Array.isArray(visit?.checklist) && visit.checklist.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-bold">Checklist</h2>
                <span className="text-xs text-muted-foreground">
                  {visit.checklist.filter(i => i.checked).length} / {visit.checklist.length} complete
                  {visit.is_checklist_complete && <span className="ml-2 text-emerald-600 font-semibold">✓ Done</span>}
                </span>
              </div>
              <ul className="space-y-2">
                {visit.checklist.filter(i => i.text).map((item, idx) => (
                  <li
                    key={item.id || idx}
                    className="flex items-start gap-3 text-sm"
                    data-testid={`checklist-item-${idx}`}
                  >
                    <span
                      className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 ${
                        item.checked
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {item.checked && <Check className="w-3 h-3" />}
                    </span>
                    <span className={item.checked ? 'text-gray-500 line-through' : 'text-foreground'}>
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Photos Section */}
          {allPhotos.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h2 className="font-heading text-lg font-bold mb-4">Photos ({allPhotos.length})</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {allPhotos.map((photo, idx) => {
                  // Resolve absolute URL: server path (preferred) → dataUrl
                  // → string. Prepend REACT_APP_BACKEND_URL on relative paths.
                  let photoUrl = '';
                  if (typeof photo === 'string') {
                    photoUrl = photo;
                  } else {
                    photoUrl = photo?.url || photo?.dataUrl || '';
                  }
                  if (photoUrl && !photoUrl.startsWith('data:') && !photoUrl.startsWith('http')) {
                    photoUrl = `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`;
                  }
                  if (!photoUrl) {
                    // Photo entry exists but has no URL (legacy placeholder
                    // from a prior bug). Show an honest fallback instead of
                    // a silent broken <img>.
                    return (
                      <div
                        key={idx}
                        className="aspect-square rounded-xl border border-dashed border-amber-300 bg-amber-50 flex flex-col items-center justify-center text-amber-700 px-2 text-center"
                        data-testid={`photo-missing-${idx}`}
                      >
                        <AlertTriangle className="w-5 h-5 mb-1" />
                        <span className="text-[10px] leading-tight">Photo unavailable</span>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={idx}
                      onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                      className="aspect-square rounded-xl overflow-hidden border border-gray-100 hover:opacity-80 transition-opacity relative group"
                    >
                      <img
                        src={photoUrl}
                        alt={`Photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Broken URL — replace silently broken img with a
                          // visible fallback so users know SOMETHING is off
                          e.currentTarget.style.display = 'none';
                          const sib = e.currentTarget.nextElementSibling;
                          if (sib) sib.style.display = 'flex';
                        }}
                      />
                      <div
                        className="absolute inset-0 hidden flex-col items-center justify-center bg-amber-50 text-amber-700 text-center px-2"
                        data-testid={`photo-broken-${idx}`}
                      >
                        <AlertTriangle className="w-5 h-5 mb-1" />
                        <span className="text-[10px] leading-tight">Image link broken</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sketches Section */}
          {((fieldNotesData?.sketches?.length || 0) > 0 || (visit?.sketches?.length || 0) > 0) && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="sketches-section">
              <h2 className="font-heading text-lg font-bold mb-4">
                Sketches ({(fieldNotesData?.sketches || visit?.sketches || []).length})
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(fieldNotesData?.sketches || visit?.sketches || []).map((sketch, idx) => {
                  const sketchUrl = typeof sketch === 'string' ? sketch : (sketch.dataUrl || sketch.url || '');
                  return (
                    <div key={idx} className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                      {sketchUrl && (
                        <img
                          src={sketchUrl}
                          alt={`Sketch ${idx + 1}`}
                          className="w-full h-40 object-contain p-1"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info Card */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h2 className="font-heading text-lg font-bold mb-4">Customer</h2>
            
            <Link 
              to={`/clients/${visit.client_id}`}
              className="block hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg transition-colors"
            >
              <p className="font-semibold text-foreground">{visit.client_name}</p>
            </Link>
            
            {visit.client_phone && (
              <a 
                href={`tel:${visit.client_phone}`}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary mt-3"
              >
                <Phone className="w-4 h-4" />
                {visit.client_phone}
              </a>
            )}
            
            {visit.client_email && (
              <a 
                href={`mailto:${visit.client_email}`}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary mt-2"
              >
                <Mail className="w-4 h-4" />
                {visit.client_email}
              </a>
            )}
          </div>

          {/* Staff Info */}
          {isStaff && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Staff Access</p>
                  <p className="text-sm text-amber-700 mt-1">
                    When you convert this to an estimate, it will be sent to a manager for review before the customer sees it.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Delete Button */}
          {visit.status !== 'converted' && (
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="w-full rounded-xl text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Site Visit
            </Button>
          )}
        </div>
      </div>

      {/* Photo Lightbox */}
      {lightboxOpen && allPhotos.length > 0 && (
        <PhotoLightbox
          photos={allPhotos.map(p => {
            // Resolve to absolute URL — same logic as the editor.
            if (typeof p === 'string') {
              if (p.startsWith('data:') || p.startsWith('http')) return p;
              return `${process.env.REACT_APP_BACKEND_URL}${p}`;
            }
            const u = p?.url || p?.dataUrl || '';
            if (!u) return '';
            if (u.startsWith('data:') || u.startsWith('http')) return u;
            return `${process.env.REACT_APP_BACKEND_URL}${u}`;
          }).filter(Boolean)}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('delete_site_visit')}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this site visit? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Estimate Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('convert_to_estimate_title')}</DialogTitle>
            <DialogDescription>
              This will create a new estimate draft using the information from this site visit.
              {isStaff && (
                <span className="block mt-2 text-amber-600">
                  As a staff member, the estimate will be sent for manager review.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConvertToEstimate} style={{ backgroundColor: primaryColor }}>
              <FileText className="w-4 h-4 mr-2" />
              Create Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Confirm Dialog */}
      {ConfirmDialogComponent}
    </div>
  );
};

export default SiteVisitDetailPage;
